import React, { useState, useCallback, useEffect, useRef } from 'react';
import '../css/mediaPage.css';
import ApiGet from '../../../../../api-controller-mod/api-serv/get-api/getApi';
import SelectField from '../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField';
import SearchField from '../../../../../component-controller-mod/component-serv/input-fs-cp/search-field-op/ts/searchField';
import { Button, Confirmation, Empty, Table, Pagination } from '@core/uilibrary';
import SwitchMedia, { SwitchMediaView } from '../../../../../component-controller-mod/component-serv/button-fs-cp/swithc-display-button-op/ts/switchMedia';
import CancelButton from '../../../../../component-controller-mod/component-serv/button-fs-cp/cancel-button-op/ts/cancelButton';
import MediaCard from '../../../../../component-controller-mod/component-serv/card-fs-cp/media-card-op/ts/mediaCard';
import UploadMediaOverlay, {
  UploadMediaFormData, ConfirmConfig,
} from '../../../../../component-controller-mod/component-serv/texteditor-fs-cp/upload-media-overlay/ts/uploadMediaOverlay';
import ViewMediaOverlay from '../../../../../component-controller-mod/component-serv/texteditor-fs-cp/view-media-overlay/ts/viewMediaOverlay';
import EditMediaOverlay, { EditMediaFormData } from '../../../../../component-controller-mod/component-serv/texteditor-fs-cp/edit-media-overlay/ts/editMediaOverlay';
import { useGlobalPopupProvider } from '../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import {
  DeleteConfirmationIcon, EditIcon, EyeIcon, SuccessEditConfirmationIcon, TrashIcon, UploadIcon, VideoIcon,
  WarningEditConfirmationIcon,
} from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

import {
  MEDIA_TYPE_OPTIONS, STATUS_OPTIONS,
  MEDIA_TABLE_COLUMNS, MEDIA_TABLE_COLUMN_WIDTHS, MediaItem,
} from './mediaData';
import { ApiMediaItem, uploadMediaApi, deleteMediaApi, editMediaFileApi, editMediaTextApi } from './mediaApi';
import { mapApiItem, toBadgeVariant } from './mediaHelpers';
import { CONFIRM_ICONS, FileNameCell, StatusCell } from './mediaComponents';
import { API_ENDPOINTS } from '../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';
import { groupPermissionAccessMedia } from '../../../../../context-controller-mod/context-serv/permission-provider/permissionExporter';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;
const GRID_PAGE_SIZE = 12; // items per scroll batch

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────

const MediaPage = () => {
  const [viewMode, setViewMode] = useState<SwitchMediaView>('grid');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // ── Table state (server-side pagination) ───────────────────────────────────
  const [tableItems, setTableItems] = useState<MediaItem[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tablePage, setTablePage] = useState(DEFAULT_PAGE);
  const [tableSize, setTableSize] = useState(DEFAULT_PAGE_SIZE);
  const [tableTotal, setTableTotal] = useState(0);

  // ── Grid state (infinite scroll) ───────────────────────────────────────────
  const [gridItems, setGridItems] = useState<MediaItem[]>([]);
  const [gridLoading, setGridLoading] = useState(false);
  const [gridLoadingMore, setGridLoadingMore] = useState(false);
  const [gridPage, setGridPage] = useState(DEFAULT_PAGE);
  const [gridHasMore, setGridHasMore] = useState(true);
  const gridObserverRef = useRef<IntersectionObserver | null>(null);

  const canCreate = groupPermissionAccessMedia.create()
  const canUpdate = groupPermissionAccessMedia.update()
  const canDelete = groupPermissionAccessMedia.delete()

  const { openPopup, closePopup, openConfirm, closeConfirm } = useGlobalPopupProvider();
  const { showToast } = useToast();

  // ── Fetch for table (replaces list on each call) ───────────────────────────
  const fetchTablePage = useCallback(async (page: number, size: number) => {
    try {
      setTableLoading(true);
      const res = await ApiGet<{ success: boolean; data: { items: ApiMediaItem[]; pagination: { totalRecords: number } } }>(
        `${API_ENDPOINTS.GET_ALL_MEDIA}?page=${page}&size=${size}`
      );
      const items = res?.data?.data?.items ?? [];
      const total = res?.data?.data?.pagination?.totalRecords ?? 0;
      setTableItems(items.map(mapApiItem));
      setTableTotal(total);
    } catch (e) {
      console.error('[Media] table fetch failed:', e);
      showToast({ status: 'error', title: 'Error', description: 'Failed to load media files.' });
    } finally {
      setTableLoading(false);
    }
  }, [showToast]);

  // ── Fetch for grid (appends on scroll, resets on page 1) ──────────────────
  const fetchGridPage = useCallback(async (pageNumber: number, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setGridLoadingMore(true);
      } else {
        setGridLoading(true);
      }
      const res = await ApiGet<{ success: boolean; data: { items: ApiMediaItem[]; pagination: { hasNext: boolean } } }>(
        `${API_ENDPOINTS.GET_ALL_MEDIA}?page=${pageNumber}&size=${GRID_PAGE_SIZE}`
      );
      const items = res?.data?.data?.items ?? [];
      const hasNext = res?.data?.data?.pagination?.hasNext ?? false;
      const mapped = items.map(mapApiItem);
      setGridItems(prev => isLoadMore ? [...prev, ...mapped] : mapped);
      setGridHasMore(hasNext);
    } catch (e) {
      console.error('[Media] grid fetch failed:', e);
      showToast({ status: 'error', title: 'Error', description: 'Failed to load media files.' });
      if (!isLoadMore) setGridItems([]);
    } finally {
      if (isLoadMore) {
        setGridLoadingMore(false);
      } else {
        setGridLoading(false);
      }
    }
  }, [showToast]);

  // ── Table: refetch when page or size changes ───────────────────────────────
  useEffect(() => {
    fetchTablePage(tablePage, tableSize);
  }, [tablePage, tableSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Grid: initial load ────────────────────────────────────────────────────
  useEffect(() => {
    fetchGridPage(1);
  }, [fetchGridPage]);

  // ── Grid: load more when page increments ──────────────────────────────────
  useEffect(() => {
    if (gridPage === 1) return;
    fetchGridPage(gridPage, true);
  }, [gridPage, fetchGridPage]);

  // ── lastItemRef: attach observer to last card to trigger next page ─────────
  const lastCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (gridLoading || gridLoadingMore || !gridHasMore) return;
      if (gridObserverRef.current) gridObserverRef.current.disconnect();
      gridObserverRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setGridPage(prev => prev + 1);
        }
      });
      if (node) gridObserverRef.current.observe(node);
    },
    [gridLoading, gridLoadingMore, gridHasMore]
  );

  // ── Table pagination handlers ──────────────────────────────────────────────
  const handleTablePageChange = useCallback((newPage: number) => {
    setTablePage(newPage);
  }, []);

  const handleTableSizeChange = useCallback((newSize: number) => {
    setTablePage(DEFAULT_PAGE);
    setTableSize(newSize);
  }, []);

  // ── Filters ────────────────────────────────────────────────────────────────
  const activeMediaType = mediaTypeFilter === 'all-media' ? '' : mediaTypeFilter;
  const activeStatus = statusFilter === 'all-status' ? '' : statusFilter;
  const hasActiveFilters = Boolean(activeMediaType || activeStatus || searchQuery);

  const handleClearFilters = useCallback(() => {
    setMediaTypeFilter('');
    setStatusFilter('');
    setSearchQuery('');
    setTablePage(DEFAULT_PAGE);
    setGridPage(DEFAULT_PAGE);
    setGridHasMore(true);
    fetchGridPage(1);  // ← explicitly refetch, replaces grid atomically
  }, [fetchGridPage]);
  // Client-side filter applied on top of the current page of data
  const applyFilters = useCallback((source: MediaItem[]) =>
    source.filter((item) => {
      const matchesType = !activeMediaType || item.mediaType === activeMediaType;
      const matchesStatus = !activeStatus || item.status === activeStatus;
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesStatus && matchesSearch;
    }),
    [activeMediaType, activeStatus, searchQuery]);

  const filteredTableItems = applyFilters(tableItems);
  const filteredGridItems = applyFilters(gridItems);

  // ── Refresh both views after any mutation ──────────────────────────────────
  const refreshAll = useCallback(() => {
    fetchTablePage(tablePage, tableSize);
    setGridItems([]);
    setGridPage(1);
    setGridHasMore(true);
    fetchGridPage(1);
  }, [fetchTablePage, fetchGridPage, tablePage, tableSize]);

  // ── Delete ─────────────────────────────────────────────────────────────────
  const deleteItem = useCallback(async (id: string) => {
    const found = [...tableItems, ...gridItems].find((m) => m.id === id);
    if (!found) return;
    try {
      await deleteMediaApi(found.uuid);
      setTableItems(prev => prev.filter(m => m.id !== id));
      setGridItems(prev => prev.filter(m => m.id !== id));
      showToast({ status: 'success', title: 'Complete !', description: 'Media file was deleted successfully.' });
    } catch {
      showToast({ status: 'error', title: 'Uncomplete !', description: 'Media file was deleted unsuccessfully.' });
      refreshAll();
    }
  }, [tableItems, gridItems, showToast, refreshAll]);

  const handleDeleteConfirm = useCallback((id: string, fileName: string, fromOverlay = false) => {
    openConfirm(
      <Confirmation visible title="Delete media file"
        description={`Are you sure you want to delete this "${fileName}"? This action cannot be undone.`}
        status="error" icon={<DeleteConfirmationIcon />} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost" size="md" value="Cancel" type="button" onClick={closeConfirm} />
          <Button variant="danger" size="md" value="Delete" type="button"
            onClick={() => { closeConfirm(); if (fromOverlay) closePopup(); deleteItem(id); //NOSONAR

             }} /> 

        </>}
      />
    );
  }, [openConfirm, closeConfirm, closePopup, deleteItem]);

  const handleDelete = useCallback((id: string) => {
    const found = [...tableItems, ...gridItems].find((m) => m.id === id);
    if (!found) return;
    handleDeleteConfirm(id, found.title, false);
  }, [tableItems, gridItems, handleDeleteConfirm]);

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleEdit = useCallback((id: string) => {
    const found = [...tableItems, ...gridItems].find((m) => m.id === id);
    if (!found) return;
    const { imageSrc, title, uuid, alt, description } = found;

    const handleClose = (isDirty?: boolean) => {
      if (!isDirty) {
        closePopup();
        return;
      }
      openConfirm(
        <Confirmation
          visible
          title="Discard changes?"
          description="You have unsaved changes. Are you sure you want to discard them?"
          status="warning"
          onClose={closeConfirm}
          icon={<WarningEditConfirmationIcon/>}
          buttons={
            <>
              <Button variant="ghost" size="md" value="Cancel" type="button" onClick={closeConfirm} />
              <Button
                variant="danger"
                size="md"
                value="Discard"
                type="button"
                onClick={() => { closeConfirm(); closePopup(); }}
              />
            </>
          }
        />
      );
    };

    openPopup(
      <EditMediaOverlay
        imageSrc={imageSrc}
        fileName={title}
        altText={alt}
        description={description}
        onClose={handleClose}
        onSave={async (data: EditMediaFormData) => {
          openConfirm(
            <Confirmation
              visible
              title="Save changes?"
              description={`Are you sure you want to save changes to "${data.fileName}"?`}
              status="info"
              onClose={closeConfirm}
              icon={<SuccessEditConfirmationIcon/>}
              buttons={
                <>
                  <Button variant="ghost" size="md" value="Cancel" type="button" onClick={closeConfirm} />
                  <Button
                    variant="primary"
                    size="md"
                    value="Save"
                    type="button"
                    onClick={async () => {
                      try {
                        await editMediaTextApi(uuid, data.fileName, data.altText ?? '', data.description ?? '');
                        if (data.files && data.files.length > 0)
                          await editMediaFileApi(uuid, data.files[0], data.description ?? '');
                        refreshAll();
                        closeConfirm();
                        closePopup();
                        showToast({ status: 'success', title: 'Complete', description: 'Media file was updated successfully.' });
                      } catch {
                        closeConfirm();
                        showToast({ status: 'error', title: 'Uncomplete !', description: 'Media file was updated unsuccessfully.' });
                      }
                    }}
                  />
                </>
              }
            />
          );
        }}
      />
    );
  }, [tableItems, gridItems, openPopup, closePopup, openConfirm, closeConfirm, showToast, refreshAll]);
  // ── View ───────────────────────────────────────────────────────────────────
  const handleView = useCallback((id: string) => {
    const found = [...tableItems, ...gridItems].find((m) => m.id === id);
    if (!found) return;
    const { imageSrc, title, alt, mediaType, size, uploadBy, uploadDate, status, description } = found;

    openPopup(
      <ViewMediaOverlay
        imageSrc={imageSrc}
        fileName={title}
        altText={alt}
        fileType={mediaType}
        fileSize={size}
        uploadBy={uploadBy}
        uploadDate={uploadDate}
        description={description}
        status={toBadgeVariant(status)}
        statusLabel={status === 'deactive' ? 'Deactive' : undefined}
        onClose={closePopup}
        onDownload={async () => {
          try {
            const response = await fetch(imageSrc);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            await new Promise<void>((resolve) => {
              const link = document.createElement('a');
              link.href = blobUrl;
              link.download = title;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link); //NOSONAR
              setTimeout(() => { URL.revokeObjectURL(blobUrl); resolve(); }, 300); //NOSONAR
            });
          } catch {
            showToast({ status: 'error', title: 'Download failed', description: 'Could not download the file. Please try again.' });
          }
        }}
        onDelete={() => handleDeleteConfirm(id, title, true)}
        onEdit={() => handleEdit(id)}
      />
    );
  }, [tableItems, gridItems, openPopup, closePopup, showToast, handleDeleteConfirm, handleEdit]);

  // ── Upload ─────────────────────────────────────────────────────────────────
  const handleConfirmRequest = useCallback((config: ConfirmConfig) => {
    openConfirm(
      <Confirmation visible title={config.title} description={config.description}
        status={config.status} icon={CONFIRM_ICONS[config.status]} onClose={closeConfirm}
        buttons={<>
          <Button variant="ghost" size="md" value="Cancel" type="button"
            onClick={() => {
              closeConfirm();
              if (config.status === 'warning')
                showToast({ status: 'warning', title: 'Uncompleted !', description: 'Media file was updated unsuccessfully.' });
            }}
          />
          <Button variant="primary" size="md" value="Confirm" type="button"
            onClick={() => {
              try {
                config.onConfirm();
                closeConfirm();
                if (config.status === 'warning')
                  showToast({ status: 'error', title: 'Uncompleted !', description: 'Media file was updated unsuccessfully.' });
              } catch {
                closeConfirm();
                showToast({ status: 'error', title: 'Uncompleted !', description: 'Media file was updated unsuccessfully.' });
              }
            }}
          />
        </>}
      />
    );
  }, [openConfirm, closeConfirm, showToast]);

  const handleOpenUpload = useCallback(() => {
    openPopup(
      <UploadMediaOverlay
        onClose={closePopup}
        onConfirmRequest={handleConfirmRequest}
        acceptImagesOnly={true}
        onSave={async (data: UploadMediaFormData) => {
          try {
            await uploadMediaApi(data);
            showToast({ status: 'success', title: 'Complete', description: 'Media file was uploaded successfully.' });
            closePopup();
            setTablePage(DEFAULT_PAGE);
            refreshAll();
          } catch (e) {
            console.error('[Media] upload failed:', e);
            showToast({ status: 'error', title: 'Upload failed', description: 'Could not upload the file. Please try again.' });
          }
        }}
      />
    );
  }, [openPopup, closePopup, handleConfirmRequest, showToast, refreshAll]);

  // ── Table row actions (plain buttons matching inbox style) ─────────────────
  const renderActions = useCallback((item: MediaItem) => (
    <div className="scl--cms-pages-mediaPage-actions">
      <button
        type="button"
        className="scl--cms-pages-mediaPage-eyeBtn"
        onClick={() => handleView(item.id)}
        aria-label="View"
        title="View"
      >
        <EyeIcon />
      </button>
      {canUpdate &&
        <button
          type="button"
          className="scl--cms-pages-mediaPage-editBtn"
          onClick={() => handleEdit(item.id)}
          aria-label="Edit"
          title="Edit"
        >
          <EditIcon />
        </button>
      }

      {canDelete &&
        <>
          <span className="scl--cms-pages-mediaPage-actionDivider">|</span>
          <button
            type="button"
            className="scl--cms-pages-mediaPage-deleteBtn"
            onClick={() => handleDelete(item.id)}
            aria-label="Delete"
            title="Delete"
          >
            <TrashIcon />
          </button>
        </>
      }

    </div>
  ), [handleView, handleEdit, handleDelete]);

  const tableData = filteredTableItems.map((item, index) => ({
    id:            item.id,
    ID:            String((tablePage - 1) * tableSize + index + 1).padStart(2, '0'),
    'File name':   <FileNameCell item={item} />,
    Type:          item.mediaType.charAt(0).toUpperCase() + item.mediaType.slice(1),
    Size:          item.size,
    'Upload by':   item.uploadBy,
    'Upload date': item.uploadDate,
    Status: <StatusCell status={item.status} />,
    Action: renderActions(item),
  }));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="scl--cms-pages-mediaPage">

      {/* ── Header ── */}
      <div className="scl--cms-pages-mediaPage-header">
        <div className="scl--cms-pages-mediaPage-headerContent">
          <h1 className="scl--cms-pages-mediaPage-title">Media</h1>
          <p className="scl--cms-pages-mediaPage-description">
            Controls how content is created, reviewed, published, and managed to maintain quality and consistency.
          </p>
        </div>
        {canCreate &&
          <div className="scl--cms-pages-mediaPage-uploadeBtn">
            <Button value="Upload" icon={<UploadIcon />} onClick={handleOpenUpload} />
          </div>
        }

      </div>

      {/* ── Toolbar ── */}
      <div className="scl--cms-pages-mediaPage-toolbar">
        <div className="scl--cms-pages-mediaPage-filters">
          <div className="scl--cms-pages-mediaPage-filters-mediaType">
            <SelectField name="media-type" placeholder="All Media" label="Media Type"
              value={mediaTypeFilter} options={MEDIA_TYPE_OPTIONS}
              onChange={(value) => setMediaTypeFilter(String(value))} />
          </div>
          <div className="scl-cms-pages-mediaPage-filters-status">
            <SelectField name="status" placeholder="All status" label="Status"
              value={statusFilter} options={STATUS_OPTIONS}
              onChange={(value) => setStatusFilter(String(value))} />
          </div>
          <div className="scl--cms-pages-mediaPage-search">
            <SearchField name="search" placeholder="Search" label="Search"
              value={searchQuery} onChange={(value) => setSearchQuery(value)} />
          </div>
          {hasActiveFilters && (
            <CancelButton onClick={handleClearFilters} aria-label="Clear all filters" />
          )}
        </div>
        <SwitchMedia value={viewMode} onChange={setViewMode} />
      </div>

      {/* ── Grid view — infinite scroll ── */}
      {viewMode === 'grid' && (
        <>
          {gridLoading && (
            <div className="scl--cms-pages-mediaPage-emptyWrapper">
              <span>Loading...</span>
            </div>
          )}

          {!gridLoading && filteredGridItems.length === 0 ? (
            <div className="scl--cms-pages-mediaPage-emptyWrapper">
              <Empty
                title="No Data yet"
                description="There's nothing here yet. Start by adding new data to see it listed here."
              />
            </div>
          ) : (
            <div className="scl--cms-pages-mediaPage-grid">
              {filteredGridItems.map((item, index) => {
                const isLast = index === filteredGridItems.length - 1;
                return (
                  <div
                    key={item.id}
                    ref={isLast ? lastCardRef : null}
                    className="scl--cms-pages-mediaPage-cardWrapper"
                  >
                    <MediaCard
                      imageSrc={item.imageSrc}
                      title={item.title}
                      size={item.size}
                      isActive={item.status === 'active'}
                      onView={() => handleView(item.id)}
                      onEdit={() => handleEdit(item.id)}
                      onDelete={() => handleDelete(item.id)}
                    />
                    {item.isVideo && (
                      <div className="scl--cms-pages-mediaPage-videoOverlay">
                        <VideoIcon />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {gridLoadingMore && (
            <div className="scl--cms-pages-mediaPage-emptyWrapper">
              <span>Loading more...</span>
            </div>
          )}
          {!gridHasMore && !gridLoading && !gridLoadingMore && gridItems.length > 0 && (
            <p className="scl--cms-pages-mediaPage-noMore">No more media</p>
          )}
        </>
      )}

      {/* ── Table view — server-side pagination ── */}
      {viewMode === 'list' && (
        <>
          {filteredTableItems.length === 0 && !tableLoading ? (
            <div className="scl--cms-pages-mediaPage-emptyWrapper">
              <Empty
                title="No Data yet"
                description="There's nothing here yet. Start by adding new data to see it listed here."
              />
            </div>
          ) : (
            <div className="scl--cms-pages-mediaPage-tableWrapper">
              <Table
                columns={MEDIA_TABLE_COLUMNS}
                data={tableData}
                showCheckbox={false}
                showId={false}
                showPagination={false}
                rowsPerPageOptions={[5, 10, 20]}
                defaultRowsPerPage={tableSize}
                backgroundColor="transparent"
                selectColumnWidth={49}
                columnWidths={MEDIA_TABLE_COLUMN_WIDTHS}
              />

              <Pagination
                rowsPerPage={tableSize}
                setRowsPerPage={handleTableSizeChange}
                rowsPerPageOptions={[5, 10, 20]}
                currentPage={tablePage}
                setCurrentPage={handleTablePageChange}
                totalItems={tableTotal}
              />
            </div>
          )}

          {tableLoading && <div className="scl--cms-pages-mediaPage-tableLoading">Loading...</div>}
        </>
      )}

    </div>
  );
};

export default MediaPage;